import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

import { API } from 'aws-amplify';
import axios from 'axios';

import { 
    Button,
    Checkbox, 
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Link,
    MenuItem,
    Step,
    Stepper,
    StepLabel,
    TextField,
    Typography,
    useMediaQuery, } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import {
    MuiPickersUtilsProvider,
    DatePicker,
} from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';

import Address from '../Components/Address/Address';
import IMEIHelp from '../Components/IMEI/IMEIHelp';
import Terms from '../Components/Terms/Terms';

const initialRegistrationState = {
    defaultCity:"",
    defaultCountry:"",
    defaultPostcode: "",
    defaultLine1:"",
    defaultLine2:"",
    deviceId: "",
    deviceManufacturer: "",
    deviceModel: "",
    deviceNickname: "",
    purchaseDate: new Date(),
    purchaseLocation: "",
    purchaseReciptNumber: "",
    warrantyCode: "",
};

export default function RegisterProduct(props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const steps = t('RegistrationSteps', { returnObjects: true });
  
    const [activeStep, setActiveStep] = useState(0);
    const [deviceModels, setDeviceModels] = useState([]);
    const [loading, setLoading] = React.useState(false);

    const [isInvalidWarrantyCode, setIsInvalidWarrantyCode] = useState(null);
    const [isInvalidPurchaseLocation, setIsInvalidPurchaseLocation] = useState(null);
    const [isInvalidReciptNumber, setIsInvalidReciptNumber] = useState(null);
    // const [isInvalidPurchaseDate, setIsInvalidPurchaseDate] = useState(null);

    const [isInvalidDeviceManufacturer, setIsInvalidDeviceManufacturer] = useState(null);
    const [isInvalidDeviceModel, setIsInvalidDeviceModel] = useState(null);
    const [isInvalidDeviceId, setIsInvalidDeviceId] = useState(null);

    // const [isInvalidDefaultLine1, setIsInvalidDefaultLine1] = useState(null);
    // const [isInvalidDefaultCity, setIsInvalidDefaultCity] = useState(null);
    // const [isInvalidDefaultPostCode, setIsInvalidDefaultPostcode] = useState(null);

    const [deviceImage, setDeviceImage] = useState('');
    const [proceedNoImage, setProceedNoImage] = useState(false);
    const [showNoImageModal, setShowNoImageModal] = useState(false);
    const [agreeTandC, setAgreeTandC] = useState(false);
    const [showTAndCError, setShowTAndCError] = useState(false);

    const [
        { defaultCity, defaultCountry, defaultPostcode, defaultLine1, defaultLine2, deviceId, deviceManufacturer, deviceModel, deviceNickname, purchaseDate, purchaseLocation, purchaseReciptNumber, warrantyCode },
        setState
    ] = useState(initialRegistrationState);

    const resetRegistrationState = () => {
        setState({ ...initialRegistrationState });
    };

    const validateStep = stepI => {
      switch (stepI) {
        case 0:
          const validWarrantyCode = warrantyCode.length > 0;
          const validPurchaseLocation = purchaseLocation.length > 0;
          const validReciptNumber = purchaseReciptNumber.length > 0;
          setIsInvalidWarrantyCode(!validWarrantyCode);
          setIsInvalidPurchaseLocation(!validPurchaseLocation);
          setIsInvalidReciptNumber(!validReciptNumber);

          return (validWarrantyCode && validPurchaseLocation && validReciptNumber) 
        case 1:
          const validDeviceManufacturer = deviceManufacturer.length > 0;
          const validDeviceModel = deviceModel.length > 0;
          const validDeviceId = deviceId.length >= 15 && deviceId.length <= 19;
          setIsInvalidDeviceManufacturer(!validDeviceManufacturer);
          setIsInvalidDeviceModel(!validDeviceModel);
          setIsInvalidDeviceId(!validDeviceId);

          return (validDeviceManufacturer && validDeviceModel && validDeviceId) 
        case 2:
          const validDefaultLine1 = defaultLine1.length > 0;
          const validDefaultCity = defaultCity.length > 0;
          const validDefaultPostcode = defaultPostcode.length > 0;

          // setIsInvalidDefaultLine1(!validDefaultLine1);
          // setIsInvalidDefaultCity(!validDefaultCity);
          // setIsInvalidDefaultPostcode(!validDefaultPostcode);

          return (validDefaultLine1 && validDefaultCity && validDefaultPostcode)      
        default:
          break;
      }
    }

    const onChange = e => {
      const { name, value } = e.target;
      setState(prevState => ({ ...prevState, [name]: value }));
    }

    const setManufacturer = async e => {
      const { name, value } = e.target;
      setState(prevState => ({ ...prevState, [name]: value }));

      try {
        const models = await API.get('CrystalusionAPI', `/device/${value}`);
        models.push({Manufacturer: t('ModelNotListed'), Metadata: t('ModelNotListed')})
        setDeviceModels(models.map(model => (model.Metadata)))
        onChange({target: {name: 'deviceModel', value: ""}})
      } catch (error) {
        setDeviceModels([t('ModelNotListed')])
      }
      

    }

    const handleNext = () => {
      if (validateStep(activeStep)) {
        setActiveStep(prevActiveStep => prevActiveStep + 1)
      }
    };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleReset = () => {
    props.setRegisterProduct(false);
    setActiveStep(0);
    resetRegistrationState(null);
    setIsInvalidWarrantyCode(null);
    setIsInvalidPurchaseLocation(null);
    setIsInvalidReciptNumber(null);
    // setIsInvalidPurchaseDate(null);
    setIsInvalidDeviceManufacturer(null);
    setIsInvalidDeviceModel(null);
    setIsInvalidDeviceId(null);
    // setIsInvalidDefaultLine1(null);
    // setIsInvalidDefaultCity(null);
    // setIsInvalidDefaultPostcode(null);
    setProceedNoImage(false);
    setShowNoImageModal(false);
    setAgreeTandC(null);
    setShowTAndCError(false);
    setLoading(false);
  };

  const submitNewRegistration = async () => {
    if (!agreeTandC) {
      setShowTAndCError(true);
      return
    }

    if ((deviceImage === undefined || deviceImage.length === 0) && !proceedNoImage) {
      setShowNoImageModal(true);
      return
    }

    setLoading(true);

    const payload = {
        "Customer":{
          "Address": {
            "Line1": defaultLine1,
            "City": defaultCity,
            "Country": defaultCountry,
            "Postcode": defaultPostcode,
          }
        },
        "Device": {
            "DeviceId": deviceId,
            "Manufacturer": deviceManufacturer,
            "Model": deviceModel,
        },
        "Purchase":{
            "Date": purchaseDate,
            "Location": purchaseLocation,
            "ReciptNumber": purchaseReciptNumber
        },
        "Source": "WEB",
        "WarrantyCode": warrantyCode.toUpperCase()
    }

    if (deviceNickname.length > 0){
      payload.Device.Nickname = deviceNickname
    }

    if (defaultLine2.length > 0){
      payload.Customer.Address.Line2 = defaultLine2
    }

    const request = {
        body: payload,
    }

    try {
            const resp = await API.post('CrystalusionAPI', '/warranty', request)

            if (resp.imageUploadURL !== undefined) {
                const userDetails = await API.get('CrystalusionAPI', '/user');
                props.setUserDetails(userDetails);
                handleReset();
                
                if (deviceImage !== undefined && deviceImage.size > 0) {
                  await axios.put(resp.imageUploadURL, deviceImage);
                  setDeviceImage('');
                }
            }
        } catch (error) {
            console.log("Err:", error.response);
            if (error.response.status === 400 && error.response.data.error === "CODE_INVALID") {
              setIsInvalidWarrantyCode(true);
              setActiveStep(0);
              setLoading(false);
            }
        }

  }

  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
    case 0:
      return (
        <>
        <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="warrantyCode"
                label={t('FormLabels.WarrantyCode')}
                name="warrantyCode"
                autoComplete="warrantyCode"
                autoFocus
                value={warrantyCode}
                onChange={onChange}
                error={isInvalidWarrantyCode}
            />

            <TextField
                variant="outlined"
                margin="normal"
                select
                required
                fullWidth
                id="purchaseLocation"
                label={t('FormLabels.PurchaseLocation')}
                name="purchaseLocation"
                autoComplete="purchaseLocation"
                value={purchaseLocation}
                onChange={onChange}
                error={isInvalidPurchaseLocation}
            >
                <MenuItem value="DIRECT">Crystalusion Store</MenuItem>
                <MenuItem value="CARPHONE_WAREHOUSE">Carphone Warehouse</MenuItem>
                <MenuItem value="NOT_LISTED">Retailer Not Listed</MenuItem>
            </TextField>

            <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="purchaseReciptNumber"
                label={t('FormLabels.ReciptNumber')}
                name="purchaseReciptNumber"
                value={purchaseReciptNumber}
                onChange={onChange}
                error={isInvalidReciptNumber}
            />

            <MuiPickersUtilsProvider utils={LuxonUtils}>
                <DatePicker
                autoOk
                fullWidth
                disableFuture
                disableToolbar
                inputVariant="outlined"
                variant="dialog"
                margin="normal"
                id="purchaseDate"
                name="purchaseDate"
                label={t('FormLabels.PurchaseDate')}
                minDate={new Date(Date.now() - 12096e5)}
                value={purchaseDate}
                onChange={e => onChange({target: {name: 'purchaseDate', value: e.toISO()}})}
                />
            </MuiPickersUtilsProvider>
        </>
      );
    case 1:
      return (
        <>
        <TextField
                variant="outlined"
                margin="normal"
                select
                required
                fullWidth
                id="deviceManufacturer"
                label={t('FormLabels.DeviceManufacturer')}
                name="deviceManufacturer"
                autoComplete="deviceManufacturer"
                value={deviceManufacturer}
                onChange={setManufacturer}
                error={isInvalidDeviceManufacturer}
            >
              {
                props.deviceManufacturers.map(manufacturer => (<MenuItem key={manufacturer} value={manufacturer}>{manufacturer}</MenuItem>))
              }
            </TextField>

            <TextField
                variant="outlined"
                margin="normal"
                select
                required
                fullWidth
                id="deviceModel"
                label={t('FormLabels.DeviceModel')}
                name="deviceModel"
                autoComplete="deviceModel"
                value={deviceModel}
                onChange={onChange}
                error={isInvalidDeviceModel}
            >
              {
                deviceModels.map(model => (<MenuItem key={model} value={model}>{model}</MenuItem>))
              }
            </TextField>

            <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="deviceId"
                label={t('FormLabels.DeviceId')}
                type="tel"
                name="deviceId"
                value={deviceId}
                onChange={onChange}
                error={isInvalidDeviceId}
                helperText={isInvalidDeviceId ? t('FormLabels.DeviceIdError') : <IMEIHelp />}
            />

            <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="deviceNickname"
                label={t('FormLabels.DeviceNickname')}
                name="deviceNickname"
                value={deviceNickname}
                onChange={onChange}
                helperText={t('FormLabels.DeviceNicknameHelp')}
            />

        </>
      );
    case 2:
      return (
        <>
          <Address 
              overrideNames={true}
              fieldName="default"
              labelName="Default"
              line1={defaultLine1} 
              line2={defaultLine2} 
              city={defaultCity} 
              country={defaultCountry} 
              postcode={defaultPostcode} 
              onChange={onChange}
            />
        </>
      );
    case 3:
      return (
        <>
        <Typography variant="h3" align="center" color="inherit" style={{flexGrow: 1}}>
          {t('SmileForTheCamera.Title')}
        </Typography>
        <Typography variant="body1" align="center" color="inherit" style={{flexGrow: 1}}>
          {t('SmileForTheCamera.HelpText')} <Link target="_blank" href={t('SmileForTheCamera.Link')}>{t('SmileForTheCamera.LinkText')}</Link>
        </Typography>
        <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            type="file" accept='image/png'
            onChange={(e) => setDeviceImage(e.target.files[0])}
        />

          <Dialog
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={showNoImageModal}
            fullScreen={fullScreen}
            fullWidth={false}
            scroll="body"
            >
              <DialogContent>
                <Typography variant="h5" align="center" color="inherit" style={{flexGrow: 1}}>
                  {t('SmileForTheCamera.NoImageTitle')}
                </Typography>
                <Typography variant="body1" gutterBottom={true} align="center" color="inherit" style={{flexGrow: 1}}>
                  {t('SmileForTheCamera.NoImageBody')}
                </Typography>
                <Typography variant="body1" align="center" color="inherit" style={{flexGrow: 1}}>
                  {t('SmileForTheCamera.NoImageStartDate', {date: new Intl.DateTimeFormat(t('DateLocale')).format(Date.parse(new Date(Date.now() + 12096e5)))})}
                </Typography>
              </DialogContent>
            <DialogActions>
                <Button onClick={() => {setProceedNoImage(true); setShowNoImageModal(false)}} color="secondary">
                  {t('SmileForTheCamera.Accept')}
                </Button>
                <Button onClick={() => {setShowNoImageModal(false)}} color="primary">
                  {t('SmileForTheCamera.Decline')}
                </Button>
            </DialogActions>
          </Dialog>
          
          <FormControl error={showTAndCError}>
            <FormControlLabel
                control={<Checkbox value="agreeTandC"
                required
                color="primary"
                checked = {agreeTandC}
                onChange={(e) => setAgreeTandC(e.target.checked)} />}
                label={<Terms/>}
            />
            {showTAndCError ? (
              <FormHelperText>{t('Terms.Error')}</FormHelperText>
            ) : (
              null
            )}
        </FormControl >
        
        </>
      );
    default:
      return 'Unknown stepIndex';
    }
  }

  const getStepActions = (stepIndex) => {
    switch (stepIndex) {
    case 0:
      return (
        <>
        <Button onClick={handleReset} color="secondary">
          {t('Cancel')}
        </Button>
        <Button onClick={handleNext} color="primary">
          {t('Next')}
        </Button>
        </>
      );
    case 1:
      return (
        <>
        <Button onClick={handleBack}>
          {t('Back')}
        </Button>
        <Button onClick={handleNext} color="primary">
          {t('Next')}
        </Button>
        </>
      );
    case 2:
      return (
        <>
        <Button onClick={handleBack}>
          {t('Back')}
        </Button>
        <Button onClick={handleNext} color="primary">
          {t('Next')}
        </Button>
        </>
      );
    case 3:
      return (
        <>
        <Button onClick={handleBack}>
          {t('Back')}
        </Button>
        <Button onClick={submitNewRegistration} color="primary" disabled={loading}>
          {t('Register')}
          &nbsp;
          {loading && <CircularProgress size={24}/>}
        </Button>
        
        </>
      );
    default:
      return 'Unknown stepIndex';
    }
  }

  return (
        <Dialog
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={props.registerProduct}
        fullScreen={fullScreen}
        fullWidth={true}
        >
        <DialogTitle>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      </DialogTitle>
          <DialogContent>
            {getStepContent(activeStep)}
          </DialogContent>
        <DialogActions>
            {getStepActions(activeStep)}
        </DialogActions>
      </Dialog>
    )
}
